/**
 * Résout la destination d'une notification à partir de son couple
 * `entityType`/`entityId`, avec repli sur son `type`.
 *
 * Les notifications étaient STATIQUES : seul `entityType === "Task"` menait
 * quelque part, les 8 autres types écrits par le backend (devis, factures,
 * dettes, prospects, congés, demandes internes, évaluations, comptes équipe)
 * ne réagissaient pas au clic.
 *
 * Les valeurs d'`entityType` en base sont hétérogènes ("Task", "quote",
 * "leave_request", "leads"…) : le résolveur normalise casse, tirets,
 * underscores et pluriel plutôt que d'exiger une valeur canonique — les
 * notifications DÉJÀ ÉMISES doivent devenir cliquables, pas seulement les
 * futures.
 *
 * Module pur, sans import React : testable dans la suite Node du projet.
 */

/** entityType normalisé → route. `:id` est remplacé quand un entityId existe. */
const ENTITY_ROUTE: Record<string, { withId?: string; list: string }> = {
  task: { withId: "/tasks/:id", list: "/tasks" },
  client: { withId: "/clients/:id", list: "/clients" },
  team: { withId: "/team/:id", list: "/team" },
  member: { withId: "/team/:id", list: "/team" },
  invoice: { list: "/invoices" },
  quote: { list: "/invoices?tab=quotes" },
  lead: { list: "/leads" },
  project: { list: "/projects" },
  campaign: { list: "/marketing/campagnes" },
  debt: { list: "/compta/dettes" },
  financialtransaction: { list: "/compta/operations" },
  movement: { list: "/treasury" },
  financialaccount: { list: "/treasury" },
  subscription: { list: "/subscriptions" },
  internalrequest: { list: "/team/demandes" },
  leaverequest: { list: "/team/planning" },
  evaluation: { list: "/team/performance" },
  memberpayment: { list: "/team" },
};

/** Repli quand la notification ne porte pas d'entité exploitable. */
const TYPE_ROUTE: Record<string, string> = {
  PAYMENT_PENDING: "/invoices",
  WALLET_LOW: "/marketing/budget",
  TASK_OVERDUE: "/tasks",
  SUBSCRIPTION_RENEWAL: "/subscriptions",
};

function normalize(entityType: string): string {
  const flat = entityType.toLowerCase().replace(/[-_\s]/g, "");
  // Pluriel simple ("leads" → "lead") — sauf si le singulier n'est pas connu
  // (ex. « campaigns » et « campaign » sont tous deux vus en base).
  if (flat.endsWith("s") && ENTITY_ROUTE[flat.slice(0, -1)]) return flat.slice(0, -1);
  return flat;
}

/**
 * `null` = aucune destination connue : la ligne reste non cliquable plutôt
 * que d'envoyer l'utilisateur sur une page arbitraire.
 */
export function notificationTarget(
  entityType: string | null | undefined,
  entityId: string | null | undefined,
  type?: string | null,
): string | null {
  if (entityType) {
    const route = ENTITY_ROUTE[normalize(entityType)];
    if (route) {
      if (entityId && route.withId) return route.withId.replace(":id", entityId);
      return route.list;
    }
  }
  if (type && TYPE_ROUTE[type]) return TYPE_ROUTE[type];
  return null;
}
